import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const CartContext = createContext(null);
const KEY = "novifood_cart";
const VAT_KEY = "novifood_vat_rate";
const DEFAULT_VAT_RATE = 0.23;

export function eur(cents) {
    return ((cents || 0) / 100).toLocaleString("pt-PT", { style: "currency", currency: "EUR" });
}

/**
 * Stable line-item identity:
 *   - Product: <product_id>|<sorted-selected-options-hash>
 *   - Bundle:  bundle|<bundle_id>
 * Same product with different options → different lines. Same product with the
 * exact same options → merged (quantity increments).
 */
function lineKeyFor(entry) {
    if (entry.bundle_id) return `bundle|${entry.bundle_id}`;
    const opts = entry.selected_options || {};
    const flat = Object.keys(opts).sort().map((k) => `${k}=${opts[k]}`).join(";");
    return `p|${entry.product_id}|${flat}`;
}

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            const raw = JSON.parse(localStorage.getItem(KEY)) || [];
            // Migrate older items that don't have a line_key
            return raw.map((x) => ({ ...x, line_key: x.line_key || lineKeyFor(x) }));
        } catch {
            return [];
        }
    });
    const [open, setOpen] = useState(false);
    /* Cached VAT rate from server. Hydrated from localStorage first (avoids
       flash of wrong total), then refreshed once per session from /api/shop/config. */
    const [vatRate, setVatRate] = useState(() => {
        try {
            const cached = parseFloat(localStorage.getItem(VAT_KEY));
            return Number.isFinite(cached) && cached >= 0 && cached < 1 ? cached : DEFAULT_VAT_RATE;
        } catch {
            return DEFAULT_VAT_RATE;
        }
    });

    useEffect(() => {
        let alive = true;
        api.get("/shop/config")
            .then((r) => {
                if (!alive) return;
                const v = parseFloat(r?.data?.vat_rate);
                if (Number.isFinite(v) && v >= 0 && v < 1) {
                    setVatRate(v);
                    try { localStorage.setItem(VAT_KEY, String(v)); } catch { /* ignore */ }
                }
            })
            .catch(() => { /* keep cached / default */ });
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(KEY, JSON.stringify(items));
        } catch {
            /* ignore */
        }
    }, [items]);

    /**
     * add(product, qty, { selectedOptions, optionsSummary, extraCents })
     *   - selectedOptions: { group_id: choice_id }
     *   - optionsSummary:  human string ("Cor: Navy · Bateria: Plus")
     *   - extraCents: sum of the chosen option add-ons (for correct cart total)
     */
    const add = useCallback((product, qty = 1, extras = {}) => {
        const selected_options = extras.selectedOptions || null;
        const options_summary = extras.optionsSummary || "";
        const extra_cents = Number.isFinite(extras.extraCents) ? extras.extraCents : 0;
        const entry = {
            product_id: product.id,
            name: product.name,
            price_cents: (product.price_cents || 0) + extra_cents,
            base_price_cents: product.price_cents || 0,
            extra_cents,
            image_url: product.image_url,
            slug: product.slug,
            quantity: qty,
            selected_options,
            options_summary,
        };
        entry.line_key = lineKeyFor(entry);
        setItems((prev) => {
            const idx = prev.findIndex((x) => x.line_key === entry.line_key);
            if (idx >= 0) {
                const copy = [...prev];
                copy[idx] = { ...copy[idx], quantity: Math.min(99, copy[idx].quantity + qty) };
                return copy;
            }
            return [...prev, entry];
        });
        setOpen(true);
    }, []);

    /** Add a pre-configured bundle as a single cart line. */
    const addBundle = useCallback((bundle, qty = 1) => {
        const entry = {
            bundle_id: bundle.id,
            name: bundle.name,
            price_cents: bundle.price_cents,
            original_price_cents: bundle.original_price_cents,
            image_url: bundle.image_url,
            slug: bundle.slug,
            quantity: qty,
            included_items: bundle.items || [],
        };
        entry.line_key = lineKeyFor(entry);
        setItems((prev) => {
            const idx = prev.findIndex((x) => x.line_key === entry.line_key);
            if (idx >= 0) {
                const copy = [...prev];
                copy[idx] = { ...copy[idx], quantity: Math.min(99, copy[idx].quantity + qty) };
                return copy;
            }
            return [...prev, entry];
        });
        setOpen(true);
    }, []);

    const setQty = useCallback((line_key, qty) => {
        setItems((prev) => prev.map((x) => (x.line_key === line_key ? { ...x, quantity: Math.max(1, Math.min(99, qty)) } : x)));
    }, []);

    const remove = useCallback((line_key) => {
        setItems((prev) => prev.filter((x) => x.line_key !== line_key));
    }, []);

    const clear = useCallback(() => setItems([]), []);

    const count = items.reduce((s, x) => s + x.quantity, 0);
    /* All product prices in DB are NET (without VAT). Subtotal is what the
       user sees on cards / drawer lines; VAT is added at checkout. */
    const subtotalCents = items.reduce((s, x) => s + x.price_cents * x.quantity, 0);
    const vatCents = Math.round(subtotalCents * vatRate);
    const totalCents = subtotalCents + vatCents;
    const vatPercent = Math.round(vatRate * 100);

    return (
        <CartContext.Provider value={{ items, open, setOpen, add, addBundle, setQty, remove, clear, count, subtotalCents, vatCents, totalCents, vatRate, vatPercent }}>
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
}
